import { OUTLIER_COLUMNS, OUTLIER_FILTERS, Outlier } from '@features/admin/outliers/outliersProtos';
import { useOutlierActionsColumns } from '@features/admin/outliers/useOutlierActionsColumns';
import { useAdminApi } from '@hooks/useAdminApi';
import { Box, Divider } from '@mui/material';
import { BlotterWidget } from '@shared/components/BlotterWidget';
import { SimpleChart, SimpleChartProps } from '@shared/components/simple-chart/SimpleChart';
import { usePageTitle } from '@shared/hooks/usePageTitle';
import theme from '@shared/themes/darkTheme';
import { ResolutionString } from '@tradingview/types';
import { RowClickedEvent } from 'ag-grid-enterprise';
import { addMinutes } from 'date-fns';
import { BarData, Time } from 'lightweight-charts';
import { useCallback, useEffect, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';

const getRowId = ({ data }): string => data?.id?.toString();

const defaultColDef = {
  sortable: true,
  filter: false,
  floatingFilter: false,
  resizable: true,
  enableCellChangeFlash: true,
  width: 150,
};

const getPaddedStartEndDates = (start: Date, end: Date) => {
  const paddedStart = addMinutes(start, -1440);
  const paddedEnd = addMinutes(end, 1440);

  return { fromDate: paddedStart.toISOString(), toDate: paddedEnd.toISOString() };
};

export const OutlierContractAdminPage = () => {
  usePageTitle('Outliers Contract - Admin');

  const { contractSymbol } = useParams();
  const { apiClient } = useAdminApi();
  const dataLoader = apiClient?.outliersLoader(contractSymbol);
  const COLUMNS = useOutlierActionsColumns(OUTLIER_COLUMNS);
  const [searchParams] = useSearchParams();
  const periodFromUrl = searchParams.get('period');

  const [simpleChartInfo, setSimpleChartInfo] = useState<SimpleChartProps>({
    symbol: contractSymbol,
    period: periodFromUrl as ResolutionString,
  });

  const onOutlierRowClicked = (event: RowClickedEvent<Outlier, any>) => {
    if (event.data) {
      const { start, end, period } = event.data;
      const { fromDate, toDate } = getPaddedStartEndDates(new Date(start), new Date(end));

      setSimpleChartInfo({ ...simpleChartInfo, start: fromDate, end: toDate, period: period as ResolutionString });
    }
  };

  const onDailyChartClick = useCallback((datapoint: BarData<Time> | undefined) => {
    if (datapoint === undefined) return;

    const { time } = datapoint;
    const dateTime = new Date(+time * 1000);

    setSimpleChartInfo(prevInfo => {
      const currentPeriod = prevInfo.period;

      const daysToAdd = currentPeriod === '1m' ? 1 : 15;
      const { fromDate, toDate } = getPaddedStartEndDates(addMinutes(dateTime, -daysToAdd * 24 * 60), addMinutes(dateTime, daysToAdd * 24 * 60));

      return { ...prevInfo, start: fromDate, end: toDate };
    });
  }, []);

  useEffect(() => {
    setSimpleChartInfo(prevInfo => ({
      ...prevInfo,
      symbol: contractSymbol,
      period: periodFromUrl as ResolutionString,
      start: undefined,
      end: undefined,
    }));
  }, [contractSymbol, periodFromUrl]);

  if (!contractSymbol || !dataLoader) return null;

  return (
    <Box display="flex" flexDirection="column" flex={1}>
      <BlotterWidget
        blotterTitle={contractSymbol.toLocaleUpperCase()}
        dataLoader={dataLoader}
        columnDefs={COLUMNS}
        filters={OUTLIER_FILTERS}
        getRowId={getRowId}
        defaultColDef={defaultColDef}
        disableSearchFilter
        onRowClicked={onOutlierRowClicked}
        onRefreshClicked={() => window.location.reload()}
        maxHeight={450}
      />
      <Divider style={{ height: 2, backgroundColor: theme.palette.background.lighter }} />
      <SimpleChart {...simpleChartInfo} />
      <Divider style={{ height: 2, backgroundColor: theme.palette.background.lighter }} />
      <SimpleChart symbol={contractSymbol} period={'1d' as ResolutionString} onClick={onDailyChartClick} />
    </Box>
  );
};
